import WarningOutlined from "@mui/icons-material/WarningOutlined";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  LongPollingJobWrapper,
  PollingResult,
} from "components/LongPollingJobWrapper";
import { getStaffFacingActivityTitle } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityStatus, ActivityType, JobStatus } from "generated-graphql/graphql";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useGenerateCombinedPdfFromDocuments } from "routes/Staff/MailRoom/Communications/api";
import { prettyPrintDateTime } from "../../../../util/dates";
import { ActivityInputs } from "../../Fulfillment/FulfillmentDetails/Activities/ActivityInputs";
import { useCohortDetails } from "../useCohortDetails";
import { CohortActivityBottomToolbar } from "./ActivityBottomToolbar";
import { CohortDocumentDisplay } from "./CohortDocumentDisplay";
import { CohortStaffInputs } from "./CohortStaffInputs";
import { ReportDetailsExtended } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities/ReportDetailsExtended";

// Activities that don't have meaningful display info when successful
const MINIMAL_DISPLAY_ACTIVITIES = [
  ActivityType.ValidateCohort,
  ActivityType.ValidateCohortMembership,
  ActivityType.InitializeCohorts,
  ActivityType.WaitForCohorts,
  ActivityType.WaitForAllCnReports,
  ActivityType.WaitForAllCredentialReports,
  ActivityType.WaitForAllAltIdReports,
  ActivityType.WaitForAllStateReports,
  ActivityType.WaitForAllFd,
  ActivityType.WaitForAllLepc,
];

export function CohortActivityDetails() {
  const { cohortId, activityId } = useParams<{
    cohortId: string;
    activityId: string;
  }>();
  const { data } = useCohortDetails();
  const alerts = useAlerts();

  const currentActivityId =
    activityId ?? data?.cohort?.currentWorkflow?.currentActivityId;
  const activity = data?.cohort?.currentWorkflow?.activities?.find(
    (a) => a.id === currentActivityId
  );

  const { data: logData, loading: loadingActivityLog } = useActivityLogs(
    activity?.id,
    `status:${ActivityStatus.Error}`,
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const recentErrorMessage =
    logData?.activityLogs.items?.[0]?.message ?? "Unknown Error";

  // States with custom summaries in Cohort Summary — hide the generic
  // ReportDetailsExtended for these since their data is shown there instead.
  const statesToHideReportDetailsExtended = ["KS", "NM"];
  const hideReportDetailsExtended =
    statesToHideReportDetailsExtended.includes(data?.cohort?.state ?? "");

  // Combined PDF generation for Lob activities
  const {
    generateCombinedPdfFromDocuments,
    loading: generateCombinedPdfLoading,
  } = useGenerateCombinedPdfFromDocuments();
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const { fetchFile } = useCheckForFile();

  // Get document IDs from communication documents for Lob activities
  const communication = activity?.communications?.[0];
  const communicationDocumentIds =
    communication?.communicationDocuments?.map((cd: any) => cd.document.id) ?? [];

  const onGenerateCombinedPdfClick = useCallback(async () => {
    if (communicationDocumentIds.length === 0) {
      alerts.error("No documents to generate combined PDF from");
      return;
    }
    const result = await generateCombinedPdfFromDocuments(communicationDocumentIds);
    setJobId(result?.id);
    alerts.info(
      "Generating combined PDF. This may take a while, please wait..."
    );
  }, [generateCombinedPdfFromDocuments, communicationDocumentIds, alerts]);

  const pollingCompleted = useCallback(
    async (result: PollingResult) => {
      setJobId(undefined);
      if (result.pollingTimedOut) {
        alerts.error(
          "Generating combined PDF timed out. Please check the Jobs dashboard."
        );
        return;
      }
      if (result.job?.status === JobStatus.Succeeded) {
        const signedUrl = await fetchFile(result.job.output.outputKey);
        if (signedUrl) {
          window.open(signedUrl, "_blank");
        }
      }
    },
    [fetchFile, alerts]
  );

  if (!activity) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          Activity not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The requested activity could not be found in the current workflow.
        </Typography>
      </Box>
    );
  }

  // Check if this is a minimal display activity that completed successfully
  const shouldShowMinimalDisplay =
    MINIMAL_DISPLAY_ACTIVITIES.includes(activity.type) &&
    (activity.status === ActivityStatus.Completed ||
      activity.status === ActivityStatus.StaffCompleted) &&
    !activity.documents?.length &&
    !activity.payments?.length &&
    !activity.description;

  // Show minimal display for successful validation-type activities
  if (shouldShowMinimalDisplay) {
    return (
      <Paper sx={{ p: 3 }} elevation={0}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {getStaffFacingActivityTitle(activity)}
            </Typography>
            <Chip
              label={prettyPrintEnumValue(activity.status)}
              color="success"
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            This activity completed successfully with no additional details to
            display.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // Special handling for Distribute Cohort Documents activity
  if (activity.type === ActivityType.DistributeCohortDocuments) {
    return (
      <Paper sx={{ p: 3 }} elevation={0}>
        <Stack spacing={3}>
          {/* Activity Header */}
          <Box>
            <Typography variant="h5" gutterBottom>
              {getStaffFacingActivityTitle(activity)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={prettyPrintEnumValue(activity.status)}
                color={
                  activity.status === ActivityStatus.Completed ||
                  activity.status === ActivityStatus.StaffCompleted
                    ? "success"
                    : activity.status === ActivityStatus.Error
                    ? "error"
                    : activity.status === ActivityStatus.InProgress
                    ? "primary"
                    : activity.status === ActivityStatus.Canceled
                    ? "warning"
                    : "default"
                }
                size="small"
              />
            </Stack>
          </Box>

          {/* Error Message for Distribute Documents */}
          {activity.status === ActivityStatus.Error && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
              }}
            >
              {loadingActivityLog ? (
                <Stack direction="row" gap={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography
                    variant="body1"
                    color="error.main"
                    fontWeight="medium"
                  >
                    Loading error details...
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" gap={1} alignItems="center">
                  <WarningOutlined color="error" />
                  <Typography
                    variant="body1"
                    color="error.main"
                    fontWeight="medium"
                  >
                    Error: {recentErrorMessage}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          {/* Link to Documents Tab */}
          <Box>
            <Typography variant="body1" gutterBottom>
              Documents have been distributed to cohort members.
            </Typography>
            <Button
              component={RouterLink}
              to={`/staff/cohorts/${cohortId}/documents`}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              View Distributed Documents
            </Button>
          </Box>

          {/* Show documents if available */}
          {activity.documents && activity.documents.length > 0 && (
            <>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="h6">Documents Distributed</Typography>
                {activity.documents.map((doc) => (
                  <CohortDocumentDisplay document={doc} key={doc.document.id} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }} elevation={0}>
      <Stack spacing={3}>
        {/* Activity Header */}
        <Box>
          <Typography variant="h5" gutterBottom>
            {getStaffFacingActivityTitle(activity)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Activity ID: {activity.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            <Chip
              label={prettyPrintEnumValue(activity.status)}
              color={
                activity.status === ActivityStatus.Completed ||
                activity.status === ActivityStatus.StaffCompleted
                  ? "success"
                  : activity.status === ActivityStatus.Error
                  ? "error"
                  : activity.status === ActivityStatus.InProgress
                  ? "primary"
                  : activity.status === ActivityStatus.Canceled
                  ? "warning"
                  : "default"
              }
              size="small"
            />
          </Stack>
        </Box>

        {/* Error Message */}
        {activity.status === ActivityStatus.Error && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: "error.main",
            }}
          >
            {loadingActivityLog ? (
              <Stack direction="row" gap={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography
                  variant="body1"
                  color="error.main"
                  fontWeight="medium"
                >
                  Loading error details...
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" gap={1} alignItems="center">
                <WarningOutlined color="error" />
                <Typography
                  variant="body1"
                  color="error.main"
                  fontWeight="medium"
                >
                  Error: {recentErrorMessage}
                </Typography>
              </Stack>
            )}
          </Box>
        )}

        {/* For Staff activities, show user inputs (e.g., LOB fee) and staff-specific inputs */}
        {activity.type === ActivityType.Staff && (
          <>
            <ActivityInputs activity={activity} />
            <CohortStaffInputs activity={activity} />
            {!hideReportDetailsExtended && (
              <ReportDetailsExtended
                reportDetailsExtended={activity.reportDetailsExtended}
              />
            )}
          </>
        )}

        {/* Documents Section */}
        {activity.documents && activity.documents.length > 0 && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Typography variant="h6">Documents</Typography>
              {activity.documents.map((doc) => (
                <CohortDocumentDisplay document={doc} key={doc.document.id} />
              ))}
            </Stack>
          </>
        )}

        {/* Payments Section */}
        {activity.payments && activity.payments.length > 0 && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Typography variant="h6">Payments</Typography>
              {activity.payments.map((payment: any) => (
                <Box
                  key={payment.id}
                  sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Amount: ${payment.amount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: {payment.status}
                    {payment.paidAt &&
                      ` | Paid: ${prettyPrintDateTime(payment.paidAt)}`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Lob Activity Actions - Combined PDF and Mailroom Link */}
        {activity.type === ActivityType.Lob && communication && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Typography variant="h6">Mail Actions</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {communicationDocumentIds.length > 0 && (
                  <LongPollingJobWrapper
                    jobId={jobId}
                    pollingCompleted={pollingCompleted}
                    timeoutInSeconds={600}
                  >
                    <Tooltip title="Download combined PDF of all mail attachments">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onGenerateCombinedPdfClick}
                        startIcon={<PictureAsPdf />}
                        disabled={generateCombinedPdfLoading}
                      >
                        Download Combined PDF
                      </Button>
                    </Tooltip>
                  </LongPollingJobWrapper>
                )}
                <Tooltip title="View communication in Mail Room">
                  <Button
                    variant="outlined"
                    size="small"
                    component={RouterLink}
                    to={`/staff/mail-room/status/${communication.id}`}
                    startIcon={<OpenInNew />}
                  >
                    View in Mail Room
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </>
        )}

        {/* Activity Bottom Toolbar with Complete Activity Button */}
        <CohortActivityBottomToolbar
          activity={activity as any}
          allActivities={
            (data?.cohort?.currentWorkflow?.activities as any[]) ?? []
          }
        />
      </Stack>
    </Paper>
  );
}
